import { createMutation } from '@tanstack/svelte-query';
import {
  createInputDevice,
  updateInputDevice,
  deleteInputDevice,
  copyInputDeviceProgramming
} from '$lib/api/input_device';
import {
  invalidateAfterInputDeviceUpdate,
  invalidateAfterLedSceneUpdate
} from '$lib/state/invalidation';
import { getUnitScope, resolveScope, type ScopeFn } from '$lib/queries/scope';
import type { InputDeviceRequest, InputDeviceResponse } from '$lib/types/input_device';

export const createInputDeviceMutation = (scope: ScopeFn = getUnitScope()) =>
  createMutation<InputDeviceResponse, Error, InputDeviceRequest>(() => ({
    mutationFn: (request) => createInputDevice(request),
    onSuccess: () => invalidateAfterInputDeviceUpdate(resolveScope(scope))
  }));

export const updateInputDeviceMutation = (scope: ScopeFn = getUnitScope()) =>
  createMutation<InputDeviceResponse, Error, { id: number; data: Partial<InputDeviceRequest> }>(
    () => ({
      mutationFn: ({ id, data }) => updateInputDevice(id, data),
      onSuccess: () => invalidateAfterInputDeviceUpdate(resolveScope(scope))
    })
  );

export const deleteInputDeviceMutation = (scope: ScopeFn = getUnitScope()) =>
  createMutation<void, Error, number>(() => ({
    mutationFn: (id) => deleteInputDevice(id),
    onSuccess: () => invalidateAfterInputDeviceUpdate(resolveScope(scope))
  }));

export const copyInputDeviceProgrammingMutation = (scope: ScopeFn = getUnitScope()) =>
  createMutation<
    InputDeviceResponse,
    Error,
    { targetId: number; sourceDeviceId: number }
  >(() => ({
    mutationFn: ({ targetId, sourceDeviceId }) =>
      copyInputDeviceProgramming(targetId, sourceDeviceId),
    onSuccess: () => {
      const unitId = resolveScope(scope);
      invalidateAfterInputDeviceUpdate(unitId);
      invalidateAfterLedSceneUpdate(unitId);
    }
  }));
